import { neutral } from "@flat-ui";
import { Box, Typography } from "@mui/material";
import { ReactElement, isValidElement } from "react";

export interface SidePanelInfoItemProps {
  /** The title to display for this info item. */
  title?: string;

  /**
   * The value associated with the info item, which can be a string or a React element.
   * If not provided, a default value of "-" will be displayed.
   */
  value?: string | ReactElement;
}

/**
 * Displays an information item in the side panel with a title and value.
 *
 * @param {SidePanelInfoItemProps} props - The props for the info item component.
 * @param {string} props.title - The title to display for this info item.
 * @param {string | ReactElement} [props.value] - The value associated with the info item,
 * which can be a string or a React element. If not provided, a default value of "-" will be displayed.
 * @returns {JSX.Element} JSX element representing the side panel info item.
 */
export function SidePanelInfoItem({
  title,
  value,
}: SidePanelInfoItemProps): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",
        alignItems: "center",
      }}
    >
      {title && (
        <Typography
          sx={{
            fontWeight: "700",
            fontSize: "12px",
            color: neutral[700],
            width: "73px",
            letterSpacing: "normal",
          }}
          data-testid={"side-panel-info-item-title"}
        >
          {title}
        </Typography>
      )}

      <Box
        data-testid={"side-panel-info-item-value"}
        sx={{ fontSize: "12px", color: neutral[800], width: "100%" }}
      >
        {isValidElement(value) ? value : value || "-"}
      </Box>
    </Box>
  );
}
